import { createPromoCode, deletePromoCode, updatePromoCode } from 'features/PromoCodesForm/services';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { IdParamsType } from 'utils/types';
import { ROUTES } from 'features/routes';
import { RootState } from 'store';
import { getData } from 'services/api';
import { sendGAEvent } from 'utils/utils';
import { useSelector } from 'react-redux';
import { useToastNotification } from './useToastNotification';
import { useTranslation } from 'react-i18next';

export const useEditPromoCodeApi = () => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { push, goBack } = useHistory();
	const { id }: IdParamsType = useParams();
	const { showNotification, TYPES } = useToastNotification();
	const { isPunchup } = useSelector((state: RootState) => state.auth);

	const isEdit = pathname.includes('edit');

	const { data: promocode, isError: isPromocodeError } = useQuery({
		queryKey: ['promocode', id],
		queryFn: () => getData(`/public/ticketing/promocodes/${id}`),
		enabled: isEdit,
	});

	const { mutate: mutateCreatePromoCode } = useMutation(createPromoCode, {
		onSuccess: () => {
			sendGAEvent({ category: 'Journeys', action: 'Create promo code' });

			if (isPunchup) {
				goBack();
			} else {
				push(ROUTES.PROMO_CODES_PATH);
			}
		},
		onError: () =>
			showNotification({
				message: t('error-creating-promocode'),
				type: TYPES.ERROR,
			}),
	});

	const { mutate: mutateEditPromoCode } = useMutation(updatePromoCode, {
		onSuccess: () => {
			sendGAEvent({ category: 'Journeys', action: 'Edit promo code' });

			if (isPunchup) {
				goBack();
			} else {
				push(ROUTES.PROMO_CODES_PATH);
			}
		},
		onError: () =>
			showNotification({
				message: t('error-editing-promocode'),
				type: TYPES.ERROR,
			}),
	});

	const { mutate: mutateDeletePromoCode } = useMutation(deletePromoCode, {
		onSuccess: () => {
			push(ROUTES.PROMO_CODES_PATH);
		},
	});

	return {
		promocode,
		// Lets a seeded edit form (useSeededEditForm) fail open instead of spinning forever on a load error.
		isPromocodeError,
		mutateCreatePromoCode,
		mutateDeletePromoCode,
		mutateEditPromoCode,
	};
};
